/** @jsx jsx */

import { css, jsx } from "@emotion/react";
import * as React from 'react';
import { 
    Text, 
    ComponentFields, 
    ComponentParams, 
    ComponentRendering, 
    getFieldValue, 
    RichText
} from '@sitecore-jss/sitecore-jss-react';

import { SitecoreGenericTextField } from '../../interfaces';
import { Button, Icon, InputField } from "@bdl-cmn-shared-packages-npm/design-system";
import { canUseDOM } from "../../predicates";
import hexa from './hexa.svg';
import hexaMint from './hexaMint.svg';
import hexaGrey from './hexaGrey.svg';
import { breakpointMax } from "../../style";
import { TextField } from "@sitecore-jss/sitecore-jss-react-forms";
import { FormBuilder } from "../../core/components/FormBuilder/formBuilder.component";
import { renderLink } from '../../utils/helper';

export interface SubscribeToNewsletterProps { 
    fields: any;
    params?: ComponentParams;
    rendering: ComponentRendering;
}

interface HexaProps {
    icon: string;
    iconColor?: string;
}

const DefaultHexa: React.FC<HexaProps> = ({ icon, iconColor }) => {
  const style = css`
    background-image: url(${hexa});
    background-position: center center;
    margin-right: 0px;
    height: 110px;
    width: 100px;
    align-items: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    color: ${iconColor === 'green' ? '#00B86B' : iconColor || '#005670'};
    @media (max-width: ${breakpointMax}) {
      display: none;
    }
  `;
  
  return (
    <div css={style}>
      <Icon icon={icon as any} />
    </div>
  );
};

const MintHexa: React.FC<HexaProps> = ({ icon, iconColor }) => {
  const style = css`
    background-image: url(${hexaMint});
    background-position: center center;
    margin-right: 0px;
    height: 110px;
    width: 100px;
    align-items: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    color: ${iconColor === 'green' ? '#00B86B' : iconColor || '#00B86B'};
    @media (max-width: ${breakpointMax}) {
      display: none;
    }
  `;
  
  return (
    <div css={style}>
      <Icon icon={icon as any} />
    </div>
  );
};

const GreyHexa: React.FC<HexaProps> = ({ icon, iconColor }) => {
  const style = css`
    background-image: url(${hexaGrey});
    background-position: center center;
    margin-right: 0px;
    height: 110px;
    width: 100px;
    align-items: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    color: ${iconColor === 'green' ? '#00B86B' : iconColor || '#425563'};
    @media (max-width: ${breakpointMax}) {
      display: none;
    }
  `;
  
  return (
    <div css={style}>
      <Icon icon={icon as any} />
    </div>
  );
};

export const SubscribeToNewsletter: React.FunctionComponent<SubscribeToNewsletterProps> = (props: SubscribeToNewsletterProps): React.ReactElement => {
    const { fields, params, rendering } = props;
    const [visibleModal, setVisibleModal] = React.useState<boolean>(false);

    const icon: string = getFieldValue<string>(fields, 'icon', '');
    const buttonIcon: string = getFieldValue<string>(fields, 'buttonIcon', '');
    const buttonLabel: string = getFieldValue<string>(fields, 'buttonLabel', '');
    const buttonLink: any = getFieldValue<any>(fields, 'buttonLink', '');
    const backgroundColor: string = getFieldValue<string>(fields, 'backgroundColor', '');
    const hexaMint: boolean = getFieldValue<boolean>(fields, 'hexaMint', false);
    const hexaGrey: boolean = getFieldValue<boolean>(fields, 'hexaGrey', false);
    const iconColor: string = getFieldValue<string>(fields, 'iconColor', '');
    const titleColor: string = getFieldValue<string>(fields, 'titleColor', '');
    const descriptionColor: string = getFieldValue<string>(fields, 'descriptionColor', '');

    const getBackground = () => {
      if (backgroundColor === "apple100") {
        return `linear-gradient(82deg, rgba(34, 170, 95, 0.20) 0%, rgba(199, 214, 77, 0.10) 53.76%)`;
      }
      if (backgroundColor === "apple500") {
        return `linear-gradient(82deg, #166F3E 0.08%, #018C45 31.62%, #C7D64D 99.36%)`;
      }
      if (backgroundColor === "gradientGreen") {
        return `linear-gradient(83deg, #166F3E 1.21%, rgba(46, 157, 71, 0.94) 41.77%, rgba(199, 214, 77, 0.75) 65.16%, rgba(199, 214, 77, 0.00) 90.12%)`;
      }
      if (backgroundColor === "peach100") {
        return `linear-gradient(263deg, rgba(248, 159, 121, 0.00) 0%, rgba(248, 159, 121, 0.60) 100%)`;
      }
      if (backgroundColor === "peach500") {
        return `linear-gradient(82deg, #A21228 0.08%, #CB2035 31.62%, #F89F79 99.36%)`;
      }
      if (backgroundColor === "gradientPeach") {
        return `linear-gradient(83deg, rgba(248, 159, 121, 0.60) 0.17%, rgba(248, 159, 121, 0.00) 100%)`;
      }
      if (backgroundColor === "gradientGrey") {
        return `linear-gradient(90deg, #253645 11.63%, rgba(37, 54, 69, 0) 85.53%)`;
      }
      if (backgroundColor === "saphirMint") {
        return `linear-gradient(90.59deg, #263099 0.61%, #007A99 42.06%, #65EBB3 131.06%)`;
      }
      if (backgroundColor === "") {
        return '#E0F9FF';
      }
    };

    const parentContainer = css`
      display: flex;
      width: 100%;
      justify-content: center;
      padding-top: 48px;
      padding-bottom: 40px;
    `;

    const container = css`
      display: flex;
      width: 780px;
      padding: 40px;
      align-items: flex-start;
      gap: 48px;
      border-radius: 16px;
      border: 1px solid #D9DEE2;
      background: ${getBackground()};
    `;

    const style = {
      cardRight: css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 14px;
        flex: 1 0 0;
      `,
      cardRightTitle: css`
        font-family: Mont;
        font-size: 24px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        color: ${titleColor ? `#${titleColor}` : '#005670'};
      `,
      cardRightDescription: css`
        font-family: Mont;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        color: ${descriptionColor ? `#${descriptionColor}` : '#005670'};
        align-self: stretch;
      `,
      inputContainer: css`
        display: flex;
        align-items: center;
        gap: 20px;
        margin-top: 20px;
        @media (max-width: ${breakpointMax}) {
            flex-direction: column;
            width: 100%;
            gap: 16px;
        }
      `,
      cardRightButton: css`
        display: flex;
        justify-content: center;
      `,
    };

    return (
        <div css={parentContainer}>
            {visibleModal && (
                <FormBuilder
                    onClickOutside={() => setVisibleModal(false)}
                    embedCode={fields?.formCode?.value}
                >
                    <div id="childrenPart">
                        {fields?.formCode?.value && (
                            <div
                                dangerouslySetInnerHTML={{ __html: fields?.formCode?.value }}
                            />
                        )}
                    </div>
                </FormBuilder>
            )}
            <div css={container}>
                {hexaMint ? (
                    <MintHexa icon={icon} iconColor={iconColor} />
                ) : hexaGrey ? (
                    <GreyHexa icon={icon} iconColor={iconColor} />
                ) : (
                    <DefaultHexa icon={icon} iconColor={iconColor} />
                )}
                <div css={style.cardRight}>
                    <div css={style.cardRightTitle}>
                        <Text
                            field={fields && (fields.title as SitecoreGenericTextField)}
                        />
                    </div>
                    <div css={style.cardRightDescription}>
                        <RichText
                            field={fields && (fields.description as SitecoreGenericTextField)}
                        />
                    </div>
                    <div css={style.inputContainer}>
                        <div css={style.cardRightButton}>
                            {fields?.formCode?.value && (
                                <Button
                                    label={buttonLabel}
                                    iconLeft={buttonIcon as any}
                                    onClick={() => setVisibleModal(true)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
